import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calculator, Loader2, Plus, Save, Trash2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AddressInput } from "@/components/AddressInput";
import { QuoteBreakdown } from "@/components/QuoteBreakdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const emptyPlace = { address: "", formatted_address: "", place_id: null, lat: null, lng: null };

const Field = ({ label, testId, children, ...rest }) => (
  <div>
    <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">{label}</label>
    {children || (
      <Input data-testid={testId} className="h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-gold" {...rest} />
    )}
  </div>
);

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
    <h2 className="mb-6 text-lg font-medium text-slate-100">{title}</h2>
    <div className="space-y-5">{children}</div>
  </div>
);

export default function ManifestForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company, quota, refresh, refreshQuota, hasFlag } = useAuth();
  const textsOn = hasFlag("sms");
  const [vehicles, setVehicles] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [quote, setQuote] = useState(null);
  const [miles, setMiles] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "", sms_consent: false,
    pickup: emptyPlace, dropoff: emptyPlace, stops: [],
    pricing_mode: "mileage", hours_booked: 3,
    flight_number: "", airline: "", terminal: "", trip_date: "", pickup_time: "",
    vehicle_id: "", chauffeur_id: "", total_charge: "", special_instructions: "",
    corporate_account_id: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    api.get("/chauffeurs").then(({ data }) => setChauffeurs(data)).catch(() => {});
    api.get("/corporate-accounts").then(({ data }) => setAccounts(data.accounts)).catch(() => {});
    if (id) {
      api.get(`/manifests/${id}`).then(({ data }) => {
        const m = data.manifest;
        setForm({
          customer_name: m.customer_name || "", customer_phone: m.customer_phone || "",
          sms_consent: !!m.sms_consent,
          customer_email: m.customer_email || "", pickup: m.pickup || emptyPlace,
          dropoff: m.dropoff || emptyPlace, stops: (m.stops || []).map((s) => ({ ...s })),
          pricing_mode: m.pricing_mode || "mileage", hours_booked: m.hours_booked || 3,
          flight_number: m.flight_number || "", airline: m.airline || "", terminal: m.terminal || "",
          trip_date: m.trip_date || "", pickup_time: m.pickup_time || "",
          vehicle_id: m.vehicle_id || "", chauffeur_id: m.chauffeur_id || "",
          corporate_account_id: m.corporate_account_id || "",
          total_charge: "", special_instructions: m.special_instructions || "",
        });
        setQuote(m.quote || null);
        setMiles(m.quote?.miles ?? null);
      }).catch((e) => setError(errMsg(e)));
    }
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const vehicle = vehicles.find((v) => v.id === form.vehicle_id);

  const points = useMemo(
    () => [form.pickup, ...form.stops, form.dropoff],
    [form.pickup, form.stops, form.dropoff]
  );

  const getQuote = async () => {
    if (!form.vehicle_id) { setError("Select a vehicle first to price the trip."); return; }
    setQuoting(true);
    setError("");
    try {
      const { data } = await api.post("/quote", {
        vehicle_id: form.vehicle_id,
        pricing_mode: form.pricing_mode,
        pickup: form.pickup,
        dropoff: form.dropoff,
        stops: form.stops,
        hours: Number(form.hours_booked || 0),
        miles: null,
      });
      setMiles(data.miles ?? null);
      setQuote(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setQuoting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      stops: form.stops.map(({ address, formatted_address, place_id, lat, lng }) => ({ address, formatted_address, place_id, lat, lng })),
      hours_booked: Number(form.hours_booked || 0),
      miles: null,
      total_charge: Number(form.total_charge || 0),
      vehicle_id: form.vehicle_id || null,
      chauffeur_id: form.chauffeur_id || null,
      corporate_account_id: form.corporate_account_id || null,
    };
    try {
      const { data } = id
        ? await api.put(`/manifests/${id}`, payload)
        : await api.post("/manifests", payload);
      await refresh();
      refreshQuota();
      navigate(`/app/manifests/${data.id}`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const quotaFull = quota?.limit ? quota.used >= quota.limit && !id : false;
  if (quotaFull) {
    return (
      <div data-testid="manifest-quota-block" className="max-w-2xl rounded-xl border border-gold/30 bg-gold/10 p-8">
        <h1 className="text-2xl font-bold text-slate-50">This week's trips are used up</h1>
        <p className="mt-3 text-sm text-slate-300">
          You've created all {quota.limit} trips included with the {company?.plan_name} plan this week.
          Your allowance resets on Monday, or go unlimited from $1.99 a week.
        </p>
        <Button asChild className="mt-6 bg-gold text-ink-900 hover:bg-gold-light">
          <Link to="/app/plans" data-testid="quota-block-upgrade-btn">View upgrade options</Link>
        </Button>
      </div>
    );
  }

  return (
    <form data-testid="manifest-form" onSubmit={submit} className="max-w-4xl space-y-6">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">{id ? "Edit manifest" : "New manifest"}</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Trip details</h1>
      </div>

      <Section title="Customer">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <Field label="Customer name" testId="manifest-customer-name" value={form.customer_name} onChange={set("customer_name")} required placeholder="John Smith" />
          <Field label="Customer phone" testId="manifest-customer-phone" value={form.customer_phone} onChange={set("customer_phone")} />
          <label data-testid="manifest-sms-consent-label" className="flex cursor-pointer items-start gap-3 sm:col-span-2">
            <input data-testid="manifest-sms-consent" type="checkbox" checked={!!form.sms_consent}
              disabled={!textsOn}
              onChange={(e) => set("sms_consent")({ target: { value: e.target.checked } })}
              className="mt-1 h-4 w-4 accent-gold disabled:opacity-40" />
            <span className="text-xs leading-relaxed text-slate-400">
              {textsOn ? (
                <>
                  Passenger agreed to receive trip updates by text at this number (required before we can
                  text them — up to ~6 messages per trip, STOP to opt out).
                </>
              ) : (
                <>
                  Text updates are part of Professional and above. On this plan passengers get their
                  trip updates by email.
                </>
              )}
            </span>
          </label>
          {textsOn && (
            <div data-testid="manifest-phone-script" className="rounded-lg border border-ink-600 bg-ink-800/60 p-4 sm:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Read this aloud before ticking the box
            </p>
            <p className="mt-2 border-l-2 border-gold pl-3 text-xs italic leading-relaxed text-slate-300">
              “Would you like text updates for this trip at this mobile number? You'll get up to about
              six messages for this reservation — your confirmation, when your chauffeur is on the way,
              and when they arrive. Message and data rates may apply, you can reply STOP at any time to
              stop them, and it is not required in order to book. May I turn those on for you?”
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
              Tick the box only if the passenger says yes — it is recorded with the date and time. Our
              carrier registration depends on this script being read word for word.
            </p>
            </div>
          )}
          <Field label="Customer email" testId="manifest-customer-email" value={form.customer_email} onChange={set("customer_email")} placeholder="john@email.com" />
        </div>
      </Section>

      <Section title="Route & stops">
        <AddressInput label="Pickup address" testId="manifest-pickup" companyId={company?.id} value={form.pickup}
          onChange={(p) => setForm((f) => ({ ...f, pickup: p }))} placeholder="IAH Terminal C" />
        {form.stops.map((s, i) => (
          <div key={i} className="flex items-end gap-3">
            <div className="flex-1">
              <AddressInput label={`Stop ${i + 1}`} testId={`manifest-stop-${i}`} companyId={company?.id} value={s}
                onChange={(p) => setForm((f) => ({ ...f, stops: f.stops.map((x, xi) => (xi === i ? { ...x, ...p } : x)) }))} />
            </div>
            <button type="button" data-testid={`manifest-stop-remove-${i}`}
              onClick={() => setForm((f) => ({ ...f, stops: f.stops.filter((_, xi) => xi !== i) }))}
              className="mb-1 text-slate-500 hover:text-red-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="manifest-add-stop"
          onClick={() => setForm((f) => ({ ...f, stops: [...f.stops, { ...emptyPlace }] }))}
          className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
          <Plus className="mr-2 h-4 w-4" /> Add stop
        </Button>
        <AddressInput label="Drop-off address" testId="manifest-dropoff" companyId={company?.id} value={form.dropoff}
          onChange={(p) => setForm((f) => ({ ...f, dropoff: p }))} placeholder="123 Main Street, Houston, TX" />
      </Section>

      <Section title="Flight & schedule">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <Field label="Airline" testId="manifest-airline" value={form.airline} onChange={set("airline")} placeholder="United" />
          <Field label="Flight number" testId="manifest-flight" value={form.flight_number} onChange={set("flight_number")} placeholder="UA123" />
          <Field label="Terminal" testId="manifest-terminal" value={form.terminal} onChange={set("terminal")} placeholder="C" />
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Date" testId="manifest-date" type="date" value={form.trip_date} onChange={set("trip_date")} required />
          <Field label="Pickup time" testId="manifest-time" type="time" value={form.pickup_time} onChange={set("pickup_time")} required />
        </div>
      </Section>

      <Section title="Vehicle, chauffeur & price">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Vehicle">
            <select
              data-testid="manifest-vehicle"
              value={form.vehicle_id}
              onChange={(e) => {
                const v = vehicles.find((x) => x.id === e.target.value);
                setForm((f) => ({ ...f, vehicle_id: e.target.value, pricing_mode: v?.pricing_mode || f.pricing_mode }));
                setQuote(null);
              }}
              className="h-12 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">Select vehicle</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{`${v.name} · ${v.pricing_mode}`}</option>)}
            </select>
          </Field>
          <Field label="Chauffeur">
            <select
              data-testid="manifest-chauffeur"
              value={form.chauffeur_id}
              onChange={(e) => setForm((f) => ({ ...f, chauffeur_id: e.target.value }))}
              className="h-12 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">Select chauffeur</option>
              {chauffeurs.map((c) => <option key={c.id} value={c.id}>{`${c.name} · ${c.email}`}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Bill to a corporate account (optional)">
            <select
              data-testid="manifest-corporate-account"
              value={form.corporate_account_id || ""}
              onChange={(e) => setForm((f) => ({ ...f, corporate_account_id: e.target.value }))}
              className="h-12 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">Charge the passenger directly</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.name} · ${a.terms_label}${a.discount_percent ? ` · ${a.discount_percent}% off` : ""}`}
                </option>
              ))}
            </select>
            {form.corporate_account_id && (
              <p data-testid="manifest-corporate-note" className="mt-2 text-[11px] text-gold">
                No deposit and no card — this trip goes on their monthly statement.
              </p>
            )}
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <Field label="Pricing mode">
            <select
              data-testid="manifest-pricing-mode"
              value={form.pricing_mode}
              onChange={(e) => { setForm((f) => ({ ...f, pricing_mode: e.target.value })); setQuote(null); }}
              className="h-12 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="mileage">Point-to-point / mileage</option>
              <option value="hourly">Hourly charter</option>
            </select>
          </Field>
          {form.pricing_mode === "hourly" && (
            <Field label="Hours booked" testId="manifest-hours" type="number" step="0.5" value={form.hours_booked} onChange={set("hours_booked")} />
          )}
          <Field label="Manual total (optional)" testId="manifest-total" type="number" step="0.01" value={form.total_charge} onChange={set("total_charge")} placeholder="Leave blank to use the quote" />
        </div>

        <Button type="button" data-testid="manifest-quote-btn" disabled={quoting} onClick={getQuote}
          className="bg-gold text-ink-900 hover:bg-gold-light">
          {quoting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Calculator className="mr-2 h-4 w-4" /> Calculate price</>)}
        </Button>
        {quote && <QuoteBreakdown quote={quote} miles={miles} testId="manifest-quote" />}
        {vehicle && !quote && (
          <p className="text-xs text-slate-500">
            {vehicle.pricing_mode === "hourly"
              ? `${money(vehicle.hourly_rate)}/hour · ${vehicle.minimum_hours}h minimum · ${vehicle.included_miles_per_hour} mi/h included`
              : `${money(vehicle.reservation_fee)} reservation + ${money(vehicle.per_mile_rate)}/mile`}
          </p>
        )}
        <Field label="Special instructions">
          <Textarea data-testid="manifest-instructions" rows={4} value={form.special_instructions} onChange={set("special_instructions")}
            placeholder="Meet & greet inside baggage claim, hold name sign."
            className="border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
        </Field>
      </Section>

      {error && (
        <p data-testid="manifest-error" className="text-sm text-red-400">
          {error} {error.includes("Upgrade") && <Link to="/app/plans" className="underline">Go to plans</Link>}
        </p>
      )}

      <div className="flex gap-3">
        <Button data-testid="manifest-save-btn" type="submit" disabled={busy} className="h-12 bg-gold px-8 text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="mr-2 h-4 w-4" /> {id ? "Save manifest" : "Generate manifest"}</>)}
        </Button>
        <Button asChild type="button" variant="outline" className="h-12 border-ink-600 bg-transparent text-slate-300">
          <Link to="/app/manifests" data-testid="manifest-cancel-btn">Cancel</Link>
        </Button>
      </div>
    </form>
  );
}
